import React, { useState, useEffect } from "react";
import "./OffersApply.css";
import { validateOffer, fetchCESOffers } from "../../../service/apiService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTheme } from "@mui/material/styles";
import { useFormContext } from "../../contexts/FormContext";
import {
  Offer,
  OffersApplyProps,
} from "../../../feature/servicePricingTabs/components/ServiceTabDetails.interface";
import OffersAvailableModal from "../../../feature/servicePricingTabs/components/OffersAvailableModal/OffersAvailableModal";
import { Typography, Button, Box } from "@mui/material";
import { useErrorClassClassifier } from "../../hooks/useErrorClassClassifier";

export const OffersApply: React.FC<OffersApplyProps> = ({
  field,
  setErrors,
}) => {
  const theme = useTheme();
  const { formValues, setFormValues } = useFormContext();

  const [offerCode, setOfferCode] = useState("");
  const [offers, setOffers] = useState<Offer[]>([]);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [selectedOfferCode, setSelectedOfferCode] = useState<string>("");
  const [isOfferValid, setIsOfferValid] = useState<boolean | string>("");
  const [isCodeEmpty, setIsCodeEmpty] = useState<boolean>(false);
  const serviceId = formValues?.serviceScenarioType?.serviceId;
  const errorClass = useErrorClassClassifier({
    error: isCodeEmpty,
    errorClassToApply: "auto-complete-highlight input-wrapper-input",
    defaultClass: "input-wrapper-input",
  });

  useEffect(() => {
    const offerDetails = sessionStorage.getItem("offerDetails");
    if (offerDetails) {
      try {
        const offer = JSON.parse(offerDetails);
        if (offer?.couponCode) {
          setSelectedOfferCode(offer.couponCode);
        }
      } catch {}
    }
  }, []);

  const handleOfferChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOfferCode(e.target.value);
    setIsCodeEmpty(false);
    setIsOfferValid("");
  };

  const handleOfferApply = async (code?: string) => {
    const codeToValidate = code || offerCode;
    if (!codeToValidate.trim()) {
      setIsCodeEmpty(true);
      return;
    }
    setIsCodeEmpty(false);

    const result = await validateOffer(
      formValues.conceptID,
      codeToValidate,
      formValues.franchiseWebLocationId,
      formValues.serviceCategoryID,
      formValues.validate_offers_with_service_categories,
      serviceId
    );
    if (result?.success) {
      if (result?.isCouponApplicable === false) {
        setFormValues((prevValues) => ({
          ...prevValues,
          offerValidationFailed: true,
          isCouponNotApplicable: true,
        }));
        setIsOfferValid(false);
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          offerValidationFailed: false,
          isCouponNotApplicable: false,
          offerRemoved: false,
        }));
        setIsOfferValid(true);
      }
    } else {
      setIsOfferValid(false);
      setErrors((prevErrors) => ({
        ...prevErrors,
        offers: "Invalid offer code",
      }));
    }
  };

  // Helper to map offers and mark selected
  const getModalOffers = (offers: Offer[], selectedCode: string) => {
    const selected = offers.find((offer) => offer.couponCode === selectedCode);
    const rest = offers.filter((offer) => offer.couponCode !== selectedCode);
    return selected
      ? [
          { ...selected, selected: true },
          ...rest.map((offer) => ({ ...offer, selected: false })),
        ]
      : offers;
  };

  const handleViewOffers = async () => {
    const result = await fetchCESOffers(formValues);
    if (result && Array.isArray(result)) {
      setOffers(result);
    } else {
      setOffers([]);
    }
    setShowOffersModal(true);
  };

  return (
    <div className="offers-apply-wrapper">
      <div className="input-wrapper">
        <input
          type="text"
          className={errorClass}
          placeholder={field.placeholder}
          value={offerCode}
          onChange={handleOfferChange}
        />
        <Button
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "primary",
          }}
          variant="contained"
          onClick={() => handleOfferApply()}
          id="input-wrapper-button"
        >
          {field.label}
        </Button>
      </div>
      {isCodeEmpty && (
        <Typography
          variant="caption"
          className="error-message"
          sx={{
            color: theme.palette.error.main,
            mt: 1,
            ml: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {field.couponfieldRequired}
        </Typography>
      )}
      {(isOfferValid === false || formValues.offerValidationFailed) && (
        <button
          type="button"
          onClick={handleViewOffers}
          className="offer-view-cta"
        >
          {field.offerModalCTALabel}
        </button>
      )}
      {(isOfferValid === false ||
        formValues.offerValidationFailed ||
        formValues.isCouponNotApplicable) && (
        <div className="offer-error-box">
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              mb: 1,
              fontSize: "1.2rem",
              ml: 0,
              textAlign: "left",
            }}
          >
            <span className="error-container-offer">
              <div
                className="error-message special-char"
                style={{ marginBottom: "0!important" }}
              ></div>{" "}
              {formValues.isCouponNotApplicable
                ? field.couponServiceInvalidMessage
                : field.couponInvalidMessage}
            </span>
          </Typography>
        </div>
      )}
      {isOfferValid && !formValues.isCouponNotApplicable && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 2,
            ml: 1,
            color: theme.palette.success.main,
          }}
        >
          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
          <Typography
            variant="body1"
            sx={{ fontSize: "1rem", color: theme.palette.success.main }}
          >
            {field.couponSuccessMessage}
          </Typography>
        </Box>
      )}

      <OffersAvailableModal
        field={field}
        isOpen={showOffersModal}
        onClose={() => setShowOffersModal(false)}
        offers={getModalOffers(offers, selectedOfferCode)}
        onApply={(code) => {
          setOfferCode(code);
          setSelectedOfferCode(code);
          setShowOffersModal(false);
          setFormValues((prevValues) => ({
            ...prevValues,
            offerRemoved: false,
          }));
        }}
        onApplyOffer={async (code) => {
          setOfferCode(code);
          await handleOfferApply(code);
        }}
      />
    </div>
  );
};
export default OffersApply;
